<template>
    <div id="adset-editor">
        <div class="col-md-12 text-center" v-if="loading">
            <i class="fa fa-spinner fa-spin inner-loading"></i>
        </div>

        <div class="row" dusk="ready" v-else>
            <div class="col-sm-10 col-sm-offset-1">
                <div class="row page-title">
                    <div class="col-md-12">
                        <h1>Create a {{ campaign.ad_product.type }} Ad Set</h1>
                    </div>
                </div>
                <form id="adSetForm">
                    <campaign-adset-editor
                        v-model="adSet"
                        :campaign="campaign"
                        :dimensions="dimensions"
                        :loan-sizes="loanSizes"
                        :base-url="baseUrl"
                        :validation="validation"
                        :features="features"
                        v-on:validate="setValidation"
                        :enable-loan-sizes="enableLoanSizes"
                        :show-errors="showErrors"
                        :phone-numbers="phoneNumbers"
                        :user-roles="userRoles"
                        @scheduleHasOverlaps="setScheduleHasOverlaps"
                        ref="adSetEditor">
                    </campaign-adset-editor>
                    <div class="row buttons" v-cloak>
                        <div class="col-md-5">
                            <button id="save_ad_set" v-on:click="saveAndReturn()" type="button" class="btn btn-block --gray --gray-border">Save Ad Set</button>
                            <p>Save progress and go back to the summary page.</p>
                        </div>
                        <div class="col-md-5 col-md-offset-2">
                            <button id="go_back_campaign_manager" v-on:click="goBackLink()" type="button" class="btn btn-block --gray --gray-border">Cancel</button>
                            <p>Cancel and go to the campaign manager.</p>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import campaignCommon from 'js/campaigns/common'
    import adSetEditor from './editor'
    import isEmpty from 'lodash/isEmpty'
    import Swal from "sweetalert2";

    export default {
        mixins: [campaignCommon, adSetEditor],

        props: ['campaign', 'dimensions', 'enable-loan-sizes', 'phoneNumbers', 'features', 'userRoles', 'expanded_attributes'],

        data() {
            return {
                adSet: {
                    id: null,
                    phone: null,
                    name: '',
                    type: null,
                    locations: [],
                    loan_purposes: [],
                    loan_sizes: [],
                    budgets: [
                        {budget: 0, budget_interval: "9"}
                    ],
                    start_date: '',
                    end_date: '',
                    bids: [],
                    tags: [],
                    is_active: null,
                    terms_cd_months: [],
                    schedule_day_ranges: [],
                    schedule_timezone: null,
                    product_family_types: null,
                    device_types: null,
                    landing_url: null,
                    opt_in_new_conforming_limit: false,
                    attributes: [],
                    expanded_attributes: this.expanded_attributes,
                    zip_codes: null
                },
                currentAdSetNames: [],
                showErrors: false,
                baseUrl: Laravel.baseUrl,
                loanSizes: [],
                validationFields: {
                    locations: false,
                    attributes: true
                },
                scheduleHasOverlaps: false,
                loading: true
            }
        },

        methods: {
            save: function (shouldRedirectToPreview) {
                $.loading.show();

                //multiselect returns mixed type; wrap in array in case a single object has been returned.
                if (!Array.isArray(this.adSet.loan_purposes)) {
                    this.adSet.loan_purposes = [this.adSet.loan_purposes];
                }

                const fields = {
                    ...this.adSet,
                    product_category_id: this.campaign.product_category.id,
                    vertical: this.campaign.vertical
                };

                if (this.adSet.expanded_attributes) {
                    const expandedAttributes = Object.entries(this.adSet.expanded_attributes).reduce((acc, [key, value]) => {
                        if (!isEmpty(value)) {
                            acc.push({
                                name: key,
                                value
                            });
                        }
                        return acc;
                    }, []);

                    fields.attributes = fields.attributes.concat(expandedAttributes);
                }

                this.$http.post(route('campaigns.adset.store', this.campaign.id), fields).then(function(response) {
                    $.loading.hide();
                    if (response.body.success) {
                        const goToUrl = route('campaigns.show', this.campaign.id);

                        this.showSuccessMessage('Ad set saved successfully!', this.redirect.bind(null, goToUrl));
                    } else {
                        this.notifyRollbar('Create AdSet Failure: components/adsets/create.vue@save: line 93', response);
                        this.showErrorMsg('Error encountered while saving the ad set.');
                    }
                },
                function (errorResponse) {
                    $.loading.hide();
                    this.notifyRollbar('Create AdSet Failure: components/adsets/create.vue@save: line 93', errorResponse);
                    this.manageErrors(errorResponse);
                });
            },

            saveAndReturn: function () {
                if (!this.isValid) {
                    this.showErrors = true;
                }

                this.$refs.adSetEditor.$validator.validateAll().then((result) => {
                    if (result && this.isValid && this.hasValidPhone && this.hasValidSchedule) {
                        this.save(false);
                    }
                })
            },

            goBackLink: function () {
                window.location = route('campaigns.show', this.campaign.id);
            },

            setScheduleHasOverlaps: function (status) {
                this.scheduleHasOverlaps = status;
            },

            loadDefaultAdSetTags: function () {
                this.adSet.tags = this.dimensions.tags.filter(x => x.is_default);
            }
        },

        computed: {
            isHomeEquity() {
                return this.campaign.product_category.id === 20;
            },

            isMortgage() {
                return this.campaign.product_category.id === 2;
            },

            isRealEstate() {
                return this.campaign.product_category.id === 27;
            },

            hasValidPhone() {
                const checkPhoneNumber = this.features.ad_set.phone && Number(_.get(this.adSet, 'type.id')) === 2;
                if (!checkPhoneNumber) {
                    return true;
                }

                return _.has(this.adSet, 'phone.id');
            },

            hasValidSchedule() {
                const haveToCheck = this.features.ad_set.phone && Number(_.get(this.adSet, 'type.id')) === 2;
                if (!haveToCheck) {
                    return true;
                }

                return !this.scheduleHasOverlaps;
            }
        },

        mounted: function () {
            Promise.all([
                this.getCurrentNames(),
                this.getLoanSizes()
            ]).then(() => {
                this.loading = false;
                this.loadDefaultAdSetTags()
            }).catch((error) => {
                new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
            });
        }
    }
</script>
